import React from "react";

export default function PnlDocumentation() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-4xl">
      <h2 className="text-lg font-bold text-gray-900 mb-6">P&L Documentation</h2>
      <div className="text-gray-700 space-y-6">
        {/* Principle */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">General Principle</h3>
          <p className="text-xs text-gray-600 leading-relaxed">
            The P&L is calculated per Business Unit. Revenue and costs are attributed to the <strong>project's BU</strong> (not the person's).
            A Tirana dev working on a Selego project → revenue AND cost are counted under Selego.
            No inter-BU transfers for now.
          </p>
        </section>

        {/* Income */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Income</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="py-1.5 pr-3 font-medium w-40">Line</th>
                  <th className="py-1.5 pr-3 font-medium">Source</th>
                  <th className="py-1.5 pr-3 font-medium">Field</th>
                  <th className="py-1.5 font-medium">Filter</th>
                </tr>
              </thead>
              <tbody className="text-gray-600">
                <tr className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 font-medium text-gray-800">Invoicing</td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">Invoice</code></td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">netTotal</code></td>
                  <td className="py-1.5">sent=yes, type=invoice, grouped by project.business_unit_id + month</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 font-medium text-gray-800">Grants</td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">Bank</code></td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">amount</code></td>
                  <td className="py-1.5">sub_category=SUBVENTION, attributed via projectName → project → BU</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Expenses */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Expenses</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="py-1.5 pr-3 font-medium w-40">Line</th>
                  <th className="py-1.5 pr-3 font-medium">Source</th>
                  <th className="py-1.5 pr-3 font-medium">Field</th>
                  <th className="py-1.5 font-medium">Filter</th>
                </tr>
              </thead>
              <tbody className="text-gray-600">
                <tr className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 font-medium text-gray-800">Employee Costs</td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">Activity</code></td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">cost</code></td>
                  <td className="py-1.5">userContract ≠ FREELANCE, projectType ∉ [admin, prospection, timeoff], grouped by project BU + month</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 font-medium text-gray-800">Freelance Monthly Costs</td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">Activity</code></td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">cost</code></td>
                  <td className="py-1.5">userContract = FREELANCE, payment_type = MONTHLY, projectType ∉ [timeoff], grouped by project BU + month</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 font-medium text-gray-800">Freelance Daily Costs</td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">Activity</code></td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">cost</code></td>
                  <td className="py-1.5">userContract = FREELANCE, payment_type = DAILY, projectType ∉ [timeoff], grouped by project BU + month</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 font-medium text-gray-800">Other Costs</td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">Bank</code></td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">amount</code></td>
                  <td className="py-1.5">category=GENERAL_COST, internal projects (selego, le collectif, ""), attributed by BU</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-1.5 pr-3 font-medium text-gray-800">Business Introducer Costs</td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">Bank</code></td>
                  <td className="py-1.5 pr-3"><code className="bg-gray-100 px-1 rounded">amount</code></td>
                  <td className="py-1.5">sub_category=BUSINESS_INTRODUCER_COST, attributed via projectName → project → BU</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Formulas */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Formulas</h3>
          <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1 font-mono">
            <p><strong>Total Income</strong> = Invoicing + Grants</p>
            <p><strong>Total Expenses</strong> = Employee Costs + Freelance Monthly + Freelance Daily + Other Costs + Business Introducer</p>
            <p><strong>Operating Profit</strong> = Total Income − Total Expenses</p>
            <p><strong>Net Profit</strong> = Operating Profit + Loan + Taxes</p>
            <p className="pt-2 border-t border-gray-200 mt-2"><strong>Year to Date</strong> = Σ actual months only (forecast months excluded)</p>
            <p><strong>Projected</strong> = (Σ actuals / nb actual months) × 12</p>
          </div>
        </section>

        {/* Columns */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Summary Columns</h3>
          <div className="space-y-1 text-xs text-gray-600">
            <div className="flex gap-2">
              <span className="font-medium text-gray-800 w-28 shrink-0">Year to Date</span>
              <p>Sum of all actual months elapsed so far. Forecast months are excluded.</p>
            </div>
            <div className="flex gap-2">
              <span className="font-medium text-gray-800 w-28 shrink-0">Projected</span>
              <p>Monthly average of actuals extrapolated to the full year (× 12).</p>
            </div>
          </div>
        </section>

        {/* Display */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Display</h3>
          <div className="space-y-1 text-xs text-gray-600">
            <p>Forecast months are shown in italic/grey. Negative values appear in <span className="text-red-600 font-medium">red</span> with a minus sign.</p>
          </div>
        </section>

        {/* Inter-BU note */}
        <section className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <h3 className="text-sm font-semibold text-amber-800 mb-1">⚠️ Inter-BU Transfers</h3>
          <p className="text-xs text-amber-700 leading-relaxed">
            The current approach attributes everything (revenue + costs) to the project's BU.
            A Tirana dev working on a Selego project = revenue AND cost counted under Selego.
            Tirana only appears for projects where it is the direct BU.
            No "inter-BU transfer" line for now — to be added if an accounting view per legal entity is needed.
          </p>
        </section>
      </div>
    </div>
  );
}
